export type SubscriptionTier = 'FREE' | 'PRO' | 'BUSINESS_PRO';

export interface SubscriptionLimits {
  profileLimit: number;
}

export const SUBSCRIPTION_LIMITS: Record<SubscriptionTier, SubscriptionLimits> = {
  FREE: {
    profileLimit: 2
  },
  PRO: {
    profileLimit: 5
  },
  BUSINESS_PRO: {
    profileLimit: 15
  }
};

export interface FieldConfig {
  name: string;
  type: string;
  required?: boolean;
  placeholder?: string;
  vCardKey?: string;
  requiredTier?: SubscriptionTier;
}

export const SUBSCRIPTION_TIERS: Record<SubscriptionTier, string[]> = {
  FREE: ['name', 'email', 'phone', 'friendlyName'],
  PRO: ['website', 'address', 'notes', 'birthday', 'nickname', 'facebook', 'instagram', 'youtube', 'city', 'state', 'postcode', 'country'],
  BUSINESS_PRO: [
    'jobTitle', 'organization', 'linkedin', 'twitter', 'tiktok', 'github', 
    'department', 'role', 'skype', 'telegram', 'whatsapp', 'interests', 'expertise',
    'googleDriveUrl', 'dropboxShareUrl', 'flickrGalleryUrl'
  ]
};

export const FIELD_CONFIGS: Record<string, FieldConfig> = {
  // FREE tier fields
  name: { name: 'Full Name', type: 'text', required: true, requiredTier: 'FREE' },
  email: { name: 'Email', type: 'email', requiredTier: 'FREE' },
  phone: { name: 'Phone Number', type: 'tel', requiredTier: 'FREE' },
  friendlyName: { name: 'Friendly Name', type: 'text', requiredTier: 'FREE' },

  // PRO tier fields
  website: { name: 'Website', type: 'url', placeholder: 'https://', requiredTier: 'PRO' },
  address: { name: 'Address', type: 'text', requiredTier: 'PRO' },
  notes: { name: 'Notes', type: 'textarea', requiredTier: 'PRO' },
  birthday: { name: 'Birthday', type: 'date', requiredTier: 'PRO' },
  nickname: { name: 'Nickname', type: 'text', requiredTier: 'PRO' },
  facebook: { name: 'Facebook', type: 'url', placeholder: 'https://facebook.com/username', requiredTier: 'PRO' },
  instagram: { name: 'Instagram', type: 'url', placeholder: 'https://instagram.com/username', requiredTier: 'PRO' },
  youtube: { name: 'YouTube', type: 'url', placeholder: 'https://youtube.com/@username', requiredTier: 'PRO' },
  city: { name: 'City', type: 'text', requiredTier: 'PRO' },
  state: { name: 'State', type: 'text', requiredTier: 'PRO' },
  postcode: { name: 'Postal Code', type: 'text', requiredTier: 'PRO' },
  country: { name: 'Country', type: 'text', requiredTier: 'PRO' },

  // BUSINESS_PRO tier fields 
  jobTitle: { name: 'Job Title', type: 'text', requiredTier: 'BUSINESS_PRO' },
  organization: { name: 'Organization', type: 'text', requiredTier: 'BUSINESS_PRO' },
  linkedin: { name: 'LinkedIn', type: 'url', placeholder: 'https://linkedin.com/in/username', requiredTier: 'BUSINESS_PRO' },
  twitter: { name: 'X (Twitter)', type: 'url', placeholder: 'https://x.com/username', requiredTier: 'BUSINESS_PRO' },
  tiktok: { name: 'TikTok', type: 'url', placeholder: 'https://tiktok.com/@username', requiredTier: 'BUSINESS_PRO' },
  github: { name: 'GitHub', type: 'url', placeholder: 'https://github.com/username', requiredTier: 'BUSINESS_PRO' },
  department: { name: 'Department', type: 'text', requiredTier: 'BUSINESS_PRO' },
  role: { name: 'Role', type: 'text', requiredTier: 'BUSINESS_PRO' },
  skype: { name: 'Skype', type: 'text', requiredTier: 'BUSINESS_PRO' },
  telegram: { name: 'Telegram', type: 'text', requiredTier: 'BUSINESS_PRO' },
  whatsapp: { name: 'WhatsApp', type: 'text', requiredTier: 'BUSINESS_PRO' },
  interests: { name: 'Interests', type: 'text', requiredTier: 'BUSINESS_PRO' },
  expertise: { name: 'Expertise', type: 'text', requiredTier: 'BUSINESS_PRO' },
  googleDriveUrl: { name: 'Google Drive Share', type: 'url', requiredTier: 'BUSINESS_PRO' },
  dropboxShareUrl: { name: 'Dropbox Share', type: 'url', requiredTier: 'BUSINESS_PRO' },
  flickrGalleryUrl: { name: 'Flickr Gallery', type: 'url', requiredTier: 'BUSINESS_PRO' }
};

// Helper function to get all fields available for a given tier
export const getAvailableFields = (tier: SubscriptionTier): string[] => {
  const tiers = Object.keys(SUBSCRIPTION_TIERS) as SubscriptionTier[];
  const tierIndex = tiers.indexOf(tier);
  
  return tiers
    .slice(0, tierIndex + 1)
    .flatMap(t => SUBSCRIPTION_TIERS[t]);
};

// Helper to check if a field is available for a given tier
export const isFieldAvailable = (field: string, tier: SubscriptionTier): boolean => {
  return getAvailableFields(tier).includes(field);
};
